// Turns an analysis into words: one short spoken observation per attempt, plus
// the detail lines shown when reviewing it. Wording stays qualitative ("behind
// target", "well behind") because the measurements are approximate.

import type { Adjustment, AttemptAnalysis, FootPlacement, FootRole, Placement } from './analysis';
import type { AnalysisParams } from './params';

export interface Feedback {
  headline: string;
  spoken: string;
  lines: string[];
}

export const OUTCOME_LABEL = {
  clean: 'Clean landing',
  adjust: 'Needs adjustment',
  unassessed: 'Could not assess',
  none: 'No pop-up detected',
} as const;

const Role = (r: FootRole) => (r === 'front' ? 'Front foot' : 'Back foot');

function magnitude(fp: FootPlacement): string {
  return fp.score > 2.2 ? 'well ' : '';
}

/** "behind target", "too far toward your toes", … or null when on target. */
export function footDirection(fp: FootPlacement, P: AnalysisParams): string | null {
  if (fp.onTarget) return null;
  const a = fp.along / P.tolAlong;
  const c = fp.across / P.tolAcross;
  if (fp.score <= 1) {
    // Position is fine; only the angle failed.
    return fp.angleError > 0 ? 'turned too far toward the nose' : 'turned too far toward the tail';
  }
  const parts: string[] = [];
  const alongPhrase = a < 0 ? 'behind target' : 'ahead of target';
  const acrossPhrase = c > 0 ? 'too far toward your toes' : 'too far toward your heels';
  if (Math.abs(a) >= Math.abs(c)) {
    parts.push(magnitude(fp) + alongPhrase);
    if (Math.abs(c) > 1) parts.push(acrossPhrase);
  } else {
    parts.push((fp.score > 2.2 ? 'well ' : '') + acrossPhrase);
    if (Math.abs(a) > 1) parts.push(alongPhrase);
  }
  return parts.join(' and ');
}

/** Whole-stance description when both feet miss in a related way; else null. */
export function stancePattern(pl: Placement, P: AnalysisParams): string | null {
  if (pl.front.onTarget || pl.rear.onTarget) return null;
  const fa = pl.front.along / P.tolAlong;
  const ra = pl.rear.along / P.tolAlong;
  const fc = pl.front.across / P.tolAcross;
  const rc = pl.rear.across / P.tolAcross;
  const alongDominant = Math.abs(fa) >= Math.abs(fc) && Math.abs(ra) >= Math.abs(rc);
  if (alongDominant && Math.abs(fa) > 1 && Math.abs(ra) > 1) {
    if (fa < 0 && ra > 0) return 'Feet closer together than your target stance';
    if (fa > 0 && ra < 0) return 'Stance wider than your target';
    return fa < 0 ? 'Both feet landed too far back' : 'Both feet landed too far forward';
  }
  if (!alongDominant && Math.abs(fc) > 1 && Math.abs(rc) > 1 && Math.sign(fc) === Math.sign(rc)) {
    return fc > 0 ? 'Both feet too far toward your toes' : 'Both feet too far toward your heels';
  }
  return null;
}

function placementSentences(pl: Placement, P: AnalysisParams): string[] {
  const pattern = stancePattern(pl, P);
  if (pattern) return [pattern + '.'];
  const out: string[] = [];
  for (const role of ['front', 'rear'] as const) {
    const d = footDirection(pl[role], P);
    out.push(d ? `${Role(role)} ${d}.` : `${Role(role)} on target.`);
  }
  return out;
}

function moveDirection(adj: Adjustment): string {
  if (Math.abs(adj.along) >= Math.abs(adj.across)) return adj.along > 0 ? 'forward' : 'back';
  return adj.across > 0 ? 'toward your toes' : 'toward your heels';
}

export function describeAdjustment(adj: Adjustment): string {
  if (adj.kind === 'pivot') {
    return `${Role(adj.foot)} pivoted ${Math.abs(Math.round(adj.rotationDeg))}° after landing (not penalised).`;
  }
  return `${Role(adj.foot)} stepped ${moveDirection(adj)} after landing${adj.corrective ? ', into position' : ''}.`;
}

export function formatSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(2)} s`;
}

export function buildFeedback(a: AttemptAnalysis, P: AnalysisParams, opts: { announceTime?: boolean } = {}): Feedback {
  const lines: string[] = [];
  const steps = a.adjustments.filter((x) => x.kind === 'step');

  if (a.initial) lines.push('Landing: ' + placementSentences(a.initial, P).join(' '));
  else if (a.issues.includes('frontHiddenAtLanding') || a.issues.includes('rearHiddenAtLanding')) {
    lines.push(`Landing: not assessed — the ${a.issues.includes('frontHiddenAtLanding') ? 'front' : 'back'} foot was out of view as it landed.`);
  }
  for (const adj of a.adjustments) lines.push(describeAdjustment(adj));
  if (a.settled) lines.push('Hold: ' + placementSentences(a.settled, P).join(' '));
  else if (a.issues.includes('noHold')) lines.push('Hold: your feet didn’t stay still for the full hold.');
  else if (a.issues.includes('holdHidden')) lines.push('Hold: not assessed — feet were out of view.');
  if (a.timing.popupMs !== null) lines.push(`Pop-up time: ${formatSeconds(a.timing.popupMs)} (first movement → both feet planted).`);
  if (a.timing.reactionMs !== null) lines.push(`Reaction: ${formatSeconds(a.timing.reactionMs)} (cue → first movement).`);
  if (a.issues.includes('lowCoverage')) lines.push('You were out of view for part of the attempt.');

  let spoken: string;
  switch (a.outcome) {
    case 'clean': {
      spoken = 'Clean landing.';
      if (opts.announceTime && a.timing.popupMs !== null) spoken += ` ${(a.timing.popupMs / 1000).toFixed(1)} seconds.`;
      break;
    }
    case 'adjust': {
      const pl = a.initial;
      if (pl && !pl.onTarget) {
        const fixed = steps.find((s) => s.corrective && !pl[s.foot].onTarget);
        if (fixed) {
          const d = footDirection(pl[fixed.foot], P) ?? 'off target';
          spoken = `${Role(fixed.foot)} landed ${d}, then stepped ${moveDirection(fixed)}.`;
        } else {
          spoken = placementSentences(pl, P).join(' ');
        }
      } else if (pl && steps.length) {
        spoken = `Good landing, then your ${steps[0].foot === 'front' ? 'front' : 'back'} foot moved.`;
      } else if (pl && a.issues.includes('noHold')) {
        spoken = 'Good landing. Now hold it still.';
      } else if (pl && a.settled && !a.settled.onTarget) {
        spoken = 'Good landing, but your feet drifted off target.';
      } else if (a.settled) {
        spoken = 'Final stance: ' + placementSentences(a.settled, P).join(' ').replace(/\.$/, '') + '.';
      } else {
        spoken = 'Needs adjustment.';
      }
      break;
    }
    case 'unassessed': {
      if (a.settled?.onTarget && !a.initial) spoken = 'Final stance on target. Couldn’t see the landing.';
      else if (a.initial?.onTarget && !a.settled) spoken = 'Landing on target. Couldn’t see the hold.';
      else if (a.issues.includes('noPose') || a.issues.includes('lowCoverage')) spoken = 'Couldn’t see you clearly. Check the camera.';
      else if (a.issues.includes('frontHiddenAtLanding')) spoken = 'Couldn’t see your front foot land.';
      else if (a.issues.includes('rearHiddenAtLanding')) spoken = 'Couldn’t see your back foot land.';
      else spoken = 'Couldn’t assess that one.';
      break;
    }
    default:
      spoken = 'No pop-up detected.';
  }
  return { headline: OUTCOME_LABEL[a.outcome], spoken, lines };
}

export const ISSUE_TEXT: Record<string, string> = {
  noPose: 'No person detected.',
  noRise: 'Didn’t see you rise from lying down.',
  noLanding: 'Didn’t see your feet land.',
  frontHiddenAtLanding: 'Front foot was hidden as it landed.',
  rearHiddenAtLanding: 'Back foot was hidden at the landing.',
  noHold: 'Feet kept moving instead of holding still.',
  holdHidden: 'Feet were hidden during the hold.',
  lowCoverage: 'You were out of view for part of the attempt.',
};
